import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Search } from 'lucide-react';
import {
  HOME_VALUES,
  NO_CURRENT_VALUE,
  REASON_LABELS,
  SHAPE_HINTS,
  SHAPE_LABELS,
  fetchHomeValueNoData,
  fetchHomeValueNoDataManufacturers,
  fetchHomeValueNoDataStats,
  fetchHomeValueNoDataVendors,
} from '../api/home_value_checks';
import type {
  HomeValueNoDataFilters,
  HomeValueNoDataRow,
  HomeValueNoDataStats,
  HomeValueReasonCode,
  VendorOption,
} from '../api/home_value_checks';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { Tooltip } from '../components/ui/tooltip';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH_COMPACT,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
} from '../components/data-table/columns';
import {
  LocationChips,
  manufacturerColumnFor,
  nsIdColumnFor,
  nsStockColumnFor,
  skuColumnFor,
  vendorColumnFor,
} from '../components/data-table/homeValueColumns';
import {
  RowActionLink,
  RowActionMenu,
  RowActions,
  type RowMenuItem,
} from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDate, fmtDateTime, fmtVendor } from '../utils/format';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

/**
 * Which skipped verdicts the list shows. `no_data` alone by default — that is
 * exactly what the BOT-421 CSV lists, so the default count reconciles with the
 * analysis; `stale` is a different kind of "we did not act" and is opt-in.
 */
type ReasonScope = 'no_data' | 'stale' | 'both';
const REASON_SCOPE_OPTIONS = [
  { value: 'no_data', label: 'No data' },
  { value: 'stale',   label: 'Stale (older than TTL)' },
  { value: 'both',    label: 'No data + stale' },
];
const REASON_SCOPE_CODES: Record<ReasonScope, HomeValueReasonCode[]> = {
  no_data: ['no_data'],
  stale:   ['stale'],
  both:    ['no_data', 'stale'],
};
function isReasonScope(v: string | null): v is ReasonScope {
  return v === 'no_data' || v === 'stale' || v === 'both';
}

const CURRENT_VALUE_OPTIONS = [
  { value: '', label: 'All' },
  ...HOME_VALUES.map(v => ({ value: v, label: v })),
  { value: NO_CURRENT_VALUE, label: 'No value set' },
];

/** The storefront promise, tinted when it is the one the client worries about. */
function CurrentValueBadge({ value }: { value: string | null }) {
  const label = value?.trim() || '(none)';
  const fast = label.replace(/^\d+\.\s*/, '').toLowerCase() === 'ships in 1-3 days';
  return (
    <Tooltip
      label={
        fast
          ? 'Promises fast shipping on an item we cannot verify — the first ones to look at'
          : 'The Home Value NetSuite carries today (unchanged by this list)'
      }
    >
      <span className="inline-flex">
        <Badge variant={fast ? 'warning' : 'neutral'}>{label}</Badge>
      </span>
    </Tooltip>
  );
}

/**
 * Home Value → No data.
 *
 * The items the rule could not decide: the page the client asked for on
 * 2026-09-04 ("blank with no dealer quantities available I'd like to still have
 * flagged to me") and the equivalent of BOT-421's `<vendor>_home_value_no_data_*.csv`.
 * Read from the live verdict on `vendor_availability` — a declined verdict never
 * becomes a proposal, so nothing here is reviewable; it is a list to work through
 * by hand, ordered by the promise the storefront makes today.
 */
export default function HomeValueNoDataPage() {
  const [searchParams] = useSearchParams();
  const initialScope = searchParams.get('reason');

  const [scope, setScope] = useState<ReasonScope>(
    isReasonScope(initialScope) ? initialScope : 'no_data',
  );
  const [filters, setFilters] = useState<HomeValueNoDataFilters>({ page: 1, per_page: 50 });
  const [vendorCodes, setVendorCodes]     = useState<string[]>([]);
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<string[]>([]);
  // The list is large (≈1,200 rows on production), so the text filter applies on
  // submit rather than on every keystroke — one request per search.
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');

  const [items, setItems]     = useState<HomeValueNoDataRow[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [stats, setStats]     = useState<HomeValueNoDataStats | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    manufacturer: false,
    decided: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchHomeValueNoData({
        ...filters,
        reason_codes: REASON_SCOPE_CODES[scope],
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        // Single-valued endpoint; the control is a multi-select for consistency
        // with the other pages, so send the most recent pick.
        manufacturer: manufacturers[0],
        q: search || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters, scope, vendorCodes, manufacturers, search]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchHomeValueNoDataVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
    fetchHomeValueNoDataManufacturers()
      .then(setManufacturerOptions)
      .catch(() => setManufacturerOptions([]));
  }, []);

  useEffect(() => {
    fetchHomeValueNoDataStats(vendorCodes).then(setStats).catch(() => setStats(null));
  }, [vendorCodes]);

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(draftSearch.trim());
    setFilters(f => ({ ...f, page: 1 }));
  }

  function resetFilters() {
    setScope('no_data');
    setVendorCodes([]);
    setManufacturers([]);
    setDraftSearch('');
    setSearch('');
    setFilters({ page: 1, per_page: filters.per_page });
  }

  const hasActiveFilters =
    scope !== 'no_data' ||
    vendorCodes.length > 0 ||
    manufacturers.length > 0 ||
    !!search ||
    !!filters.current_home_value ||
    !!filters.observed_from || !!filters.observed_to;

  const columns = useMemo<ColumnDef<HomeValueNoDataRow>[]>(() => [
    expandColumn<HomeValueNoDataRow>(),
    skuColumnFor<HomeValueNoDataRow>(),
    vendorColumnFor<HomeValueNoDataRow>(),
    manufacturerColumnFor<HomeValueNoDataRow>(),
    nsIdColumnFor<HomeValueNoDataRow>(),
    {
      id: 'current',
      meta: { label: 'Current Home Value' },
      header: 'Current Home Value',
      cell: ({ row }) => <CurrentValueBadge value={row.original.ns_home_value_now} />,
    },
    {
      id: 'reason',
      meta: { label: 'Reason' },
      header: 'Reason',
      cell: ({ row }) => {
        const code = row.original.rule_reason_code;
        if (!code) return <span className="text-xs text-zinc-300">—</span>;
        return <Badge variant="neutral">{REASON_LABELS[code] ?? code}</Badge>;
      },
    },
    {
      id: 'shape',
      meta: { label: 'What we saw' },
      header: 'What we saw',
      cell: ({ row }) => {
        const shape = row.original.shape;
        return (
          <Tooltip label={SHAPE_HINTS[shape] ?? shape}>
            <span className="whitespace-nowrap text-zinc-700">{SHAPE_LABELS[shape] ?? shape}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'locations',
      size: 440,
      meta: { label: 'Vendor stock' },
      header: 'Vendor stock',
      cell: ({ row }) => (
        <LocationChips
          locations={row.original.locations}
          vendorCode={row.original.vendor_code}
          hideSupplier
        />
      ),
    },
    {
      id: 'observed',
      meta: { label: 'Observed' },
      header: 'Observed',
      cell: ({ row }) => {
        const { observed_at, age_days } = row.original;
        return (
          <div className="whitespace-nowrap">
            <div className="text-muted-foreground">{fmtDate(observed_at)}</div>
            <div className="text-[11.5px] text-zinc-400">
              {age_days != null ? `${Math.round(age_days)}d old` : '—'}
            </div>
          </div>
        );
      },
    },
    {
      id: 'decided',
      meta: { label: 'Decided' },
      header: 'Decided',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {fmtDateTime(row.original.rule_decided_at)}
        </span>
      ),
    },
    nsStockColumnFor<HomeValueNoDataRow>(),
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH_COMPACT,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [];
        if (item.ns_item_id) {
          menuItems.push({
            label: 'Open in NetSuite',
            icon: ExternalLink,
            href: `${NS_BASE_URL}${item.ns_item_id}`,
          });
        }
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot',
            icon: Camera,
            href: item.screenshot_url,
            separatorBefore: menuItems.length > 0,
          });
        }
        return (
          <RowActions>
            {item.ns_item_id && (
              <RowActionLink
                label="Details"
                icon={Search}
                href={`/all-items/${item.ns_item_id}?from=home-value`}
              />
            )}
            {menuItems.length > 0 && <RowActionMenu items={menuItems} />}
          </RowActions>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: items,
    columns,
    // Live availability rows have no proposal id; (vendor, sku) is the natural key.
    getRowId: row => `${row.vendor_code}-${row.sku}`,
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      columnPinning: { left: ['expand', 'sku'] },
    },
  });

  const perPage    = filters.per_page ?? 50;
  const page       = filters.page ?? 1;
  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  const noData = stats?.by_reason.no_data ?? 0;
  const stale  = stats?.by_reason.stale ?? 0;
  const fastPromises = stats?.by_current_value['ships in 1-3 days'] ?? 0;

  return (
    <>
      <PageHeading title="Home Value — No data" count={total}>
        {stats && (
          <span className="text-[13px] text-muted-foreground">
            no data {noData.toLocaleString('en-US')} · stale {stale.toLocaleString('en-US')}
            {fastPromises > 0 && (
              <> · {fastPromises.toLocaleString('en-US')} of them promise 1-3 days today</>
            )}
            {stats.last_decided_at && <> · last recompute {fmtDateTime(stats.last_decided_at)}</>}
          </span>
        )}
      </PageHeading>

      <form onSubmit={applySearch}>
        <FilterBar>
          <FilterRow>
            <FilterSelect
              label="Skipped as"
              options={REASON_SCOPE_OPTIONS}
              value={scope}
              onChange={v => {
                setScope(isReasonScope(v) ? v : 'no_data');
                setFilters(f => ({ ...f, page: 1 }));
              }}
            />
            <MultiSelect
              label="Vendor"
              options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
              selected={vendorCodes}
              onChange={next => { setVendorCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
            />
            <FilterSelect
              label="Current"
              options={CURRENT_VALUE_OPTIONS}
              value={filters.current_home_value ?? ''}
              onChange={v =>
                setFilters(f => ({ ...f, current_home_value: v || undefined, page: 1 }))
              }
            />
            <MultiSelect
              label="Manufacturer"
              options={manufacturerOptions.map(m => ({ value: m, label: m }))}
              selected={manufacturers}
              onChange={next => {
                setManufacturers(next.slice(-1));
                setFilters(f => ({ ...f, page: 1 }));
              }}
              searchPlaceholder="Search manufacturers…"
            />
          </FilterRow>

          <FilterRow className="gap-4">
            <FilterField label="Search">
              <Input
                placeholder="SKU or name"
                className="w-[200px]"
                value={draftSearch}
                onChange={e => setDraftSearch(e.target.value)}
              />
              <Button type="submit" variant="outline" size="sm">
                <Search className="h-3.5 w-3.5" />
                Search
              </Button>
            </FilterField>

            <FilterField label="Observed">
              <Input
                type="date"
                className="w-[140px]"
                value={filters.observed_from ?? ''}
                onChange={e =>
                  setFilters(f => ({ ...f, observed_from: e.target.value || undefined, page: 1 }))
                }
              />
              <span className="text-zinc-300">–</span>
              <Input
                type="date"
                className="w-[140px]"
                value={filters.observed_to ?? ''}
                onChange={e =>
                  setFilters(f => ({ ...f, observed_to: e.target.value || undefined, page: 1 }))
                }
              />
            </FilterField>

            <div className="ml-auto flex items-center gap-2">
              {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
              <ColumnsMenu table={table} />
            </div>
          </FilterRow>
        </FilterBar>
      </form>

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="Nothing undecided — every item with a fresh scrape got a decision"
        renderExpanded={row => {
          const item = row.original;
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    ['Description', item.item_name ?? '—'],
                    ['Manufacturer', item.manufacturer ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{item.sku ?? '—'}</span>],
                    ['NetSuite Home Value', item.ns_home_value_now ?? '—'],
                    ['On hand / available', `${item.ns_on_hand ?? '—'} / ${item.ns_available ?? '—'}`],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor stock as scraped">
                <LocationChips locations={item.locations} vendorCode={item.vendor_code} />
                <ExpandedFields
                  rows={[
                    ['What we saw', SHAPE_LABELS[item.shape] ?? item.shape],
                    ['PDC total', item.pdc_total ?? '— (no readable number)'],
                    ['Dealer stock', item.dealer_total ?? '— (no readable number)'],
                    ['Observed', fmtDateTime(item.observed_at)],
                    ['Decided', fmtDateTime(item.rule_decided_at)],
                    [
                      'Warnings',
                      item.warnings.length
                        ? <ul className="list-disc pl-4">{item.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                        : '—',
                    ],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={item.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
